import { REACT_APP_BASE_PATH } from "../config/runtimeConfig";

const STORAGE_KEY = "cai.support.session";
export const SESSION_CHANGED_EVENT = "cai:support-session-changed";

export type SaasSession = {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  token_type?: string;
  expires_at: number;
  profile?: Record<string, unknown>;
};

function baseUrl(): string {
  return (REACT_APP_BASE_PATH || "").replace(/\/+$/, "");
}

function decodeJwtPayload(jwt?: string | null): Record<string, unknown> | null {
  if (!jwt) return null;
  try {
    const parts = jwt.split(".");
    if (parts.length < 2) return null;
    let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = (4 - (b64.length % 4)) % 4;
    if (pad) b64 += "=".repeat(pad);
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

function notify() {
  window.dispatchEvent(new Event(SESSION_CHANGED_EVENT));
}

export function loadSession(): SaasSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SaasSession) : null;
  } catch {
    return null;
  }
}

export function saveSessionFromTokens(payload: {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  tokenType?: string;
  expiresIn?: number;
}): SaasSession {
  const expiresIn = payload.expiresIn && payload.expiresIn > 0 ? payload.expiresIn : 300;
  const session: SaasSession = {
    access_token: payload.accessToken,
    refresh_token: payload.refreshToken,
    id_token: payload.idToken,
    token_type: payload.tokenType || "Bearer",
    expires_at: Math.floor(Date.now() / 1000) + expiresIn,
    profile: decodeJwtPayload(payload.accessToken) || undefined,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  notify();
  return session;
}

export function clearSaasSession() {
  localStorage.removeItem(STORAGE_KEY);
  notify();
}

export class AuthApiError extends Error {
  readonly status: number;
  readonly code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function postJson<T>(path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${baseUrl()}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body ?? {}),
    });
  } catch {
    throw new AuthApiError("Network error", 0, "NETWORK_ERROR");
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = data?.response?.message || data?.message || `Request failed (${res.status})`;
    throw new AuthApiError(message, res.status, data?.response?.businessErrorCode);
  }
  return data as T;
}

type TokenPayload = {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  tokenType?: string;
  expiresIn?: number;
};

let refreshInFlight: Promise<SaasSession | null> | null = null;

async function refreshOnce() {
  const current = loadSession();
  if (!current?.refresh_token) return null;
  try {
    const data = await postJson<TokenPayload>("/auth/refresh", { refreshToken: current.refresh_token });
    return saveSessionFromTokens(data);
  } catch {
    clearSaasSession();
    return null;
  }
}

export async function getAccessToken(): Promise<string | null> {
  const session = loadSession();
  if (!session?.access_token) return null;
  if (Date.now() / 1000 < session.expires_at - 30) return session.access_token;
  if (!refreshInFlight) {
    refreshInFlight = refreshOnce().finally(() => {
      refreshInFlight = null;
    });
  }
  const next = await refreshInFlight;
  return next?.access_token ?? null;
}

export async function loginWithPassword(username: string, password: string) {
  const data = await postJson<TokenPayload>("/auth/login", { username, password });
  return saveSessionFromTokens(data);
}

export async function logout() {
  const session = loadSession();
  try {
    if (session?.refresh_token) {
      await postJson("/auth/logout", { refreshToken: session.refresh_token });
    }
  } catch {
    // ignore
  }
  clearSaasSession();
}

export function currentProfile(): Record<string, unknown> {
  return loadSession()?.profile || {};
}
